import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { applyTheme } from '../lib/themes'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Preferences {
  theme:                  string
  navOrder:               string[]
  homeLayout:             string[]
  listSorting:            string
  favoriteItemIds:        string[]
  summaryBar:             string[]
  compactMode:            boolean
  fontSize:               string
  defaultTrackExpiration: boolean
  autoAddToListOnZero:    boolean
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

export const DEFAULT_PREFERENCES: Preferences = {
  theme:                  'warm',
  navOrder:               ['home', 'list', 'unpack', 'savings', 'household'],
  homeLayout:             ['summary', 'expiring', 'items', 'shopping_button'],
  listSorting:            'urgency',
  favoriteItemIds:        [],
  summaryBar:             ['out', 'low', 'stocked'],
  compactMode:            false,
  fontSize:               'normal',
  defaultTrackExpiration: true,
  autoAddToListOnZero:    true,
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePreferences() {
  const { user }    = useAuth()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey:  ['preferences', user?.id],
    enabled:   !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn:   async () => {
      const { data, error } = await supabase
        .from('users')
        .select('is_premium, preferences')
        .eq('id', user!.id)
        .single()
      if (error) throw error
      return data as { is_premium: boolean; preferences: Partial<Preferences> | null }
    },
  })

  const prefs: Preferences = {
    ...DEFAULT_PREFERENCES,
    ...(data?.preferences ?? {}),
  }
  const isPremium = data?.is_premium ?? false

  // ── Save ──────────────────────────────────────────────────────────────────

  async function savePreferences(partial: Partial<Preferences>) {
    if (!user) return
    const next: Preferences = { ...prefs, ...partial }

    // Optimistic update — instant UI feedback
    queryClient.setQueryData(['preferences', user.id], (old: typeof data) => ({
      ...(old ?? {}),
      preferences: next,
    }))

    // Persist to Supabase
    const { error } = await supabase
      .from('users')
      .update({ preferences: next })
      .eq('id', user.id)

    if (error) {
      console.error('savePreferences failed:', error)
      // Roll back
      queryClient.invalidateQueries({ queryKey: ['preferences', user.id] })
      return
    }

    // Apply side effects
    if (partial.theme !== undefined)       applyTheme(partial.theme)
    if (partial.compactMode !== undefined) {
      document.documentElement.classList.toggle('compact-mode', partial.compactMode)
    }
    if (partial.fontSize !== undefined) {
      const sizes: Record<string, string> = { small: '14px', normal: '16px', large: '18px' }
      document.documentElement.style.setProperty(
        '--font-size-base', sizes[partial.fontSize] ?? '16px'
      )
    }
  }

  return { ...prefs, isPremium, savePreferences, isLoading }
}
